import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { calculateUpgradeScores, enrollCustomersInUpsellWorkflows, UpgradeScore } from '@/lib/predictive-maintenance';

const PredictiveMaintenancePage: React.FC = () => {
  const [scores, setScores] = useState<UpgradeScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchScores();
  }, []);

  const fetchScores = async () => {
    setError(null);
    const { data, error } = await supabase.from('upgrade_scores').select('*');
    if (error) {
      console.warn('Failed to fetch scores', error);
      setError(error.message);
    }
    setScores(data || []);
    setLoading(false);
  };

  const handleCalculateScores = async (companyId: string) => {
    setLoading(true);
    setMessage(null);
    setError(null);
    try {
      await calculateUpgradeScores(companyId);
      await fetchScores();
      setMessage('Scores calculated successfully.');
    } catch (e: any) {
      console.warn('calculateUpgradeScores failed; using dev fallback', e);
      // Dev fallback: generate in-memory sample scores when RLS blocks inserts
      const sample: UpgradeScore[] = [
        { id: 'local-1', customer_id: 'cust_001', company_id: companyId, score: 78, reasoning: { factors: ['age', 'usage'] } },
        { id: 'local-2', customer_id: 'cust_002', company_id: companyId, score: 85, reasoning: { factors: ['service_history'] } },
        { id: 'local-3', customer_id: 'cust_003', company_id: companyId, score: 72, reasoning: { factors: ['unit_age'] } },
      ];
      setScores(sample);
      setMessage('Dev fallback: showing sample scores (DB insert likely blocked by RLS).');
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollCustomers = async (companyId: string) => {
    setMessage(null);
    setError(null);
    try {
      await enrollCustomersInUpsellWorkflows(companyId);
      setMessage('Enrollment triggered successfully.');
    } catch (e: any) {
      console.warn('enrollCustomersInUpsellWorkflows failed', e);
      setError(e?.message || 'Enrollment failed (likely RLS).');
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-4">Predictive Maintenance & Upsell Engine</h1>
      
      <div className="mb-4">
        <button onClick={() => handleCalculateScores('halesac')} className="bg-blue-500 text-white px-4 py-2 rounded mr-2">
          Calculate Scores for Hales AC
        </button>
        <button onClick={() => handleEnrollCustomers('halesac')} className="bg-green-500 text-white px-4 py-2 rounded">
          Enroll Customers for Hales AC
        </button>
      </div>

      {message && <div className="mb-4 p-3 rounded bg-green-50 text-green-700">{message}</div>}
      {error && <div className="mb-4 p-3 rounded bg-red-50 text-red-700">{error}</div>}

      {loading ? (
        <p>Loading scores...</p>
      ) : (
        <div className="space-y-4">
          {scores.map(score => (
            <div key={score.id} className="p-4 border rounded">
              <p>Customer: {score.customer_id}</p>
              <p>Score: {score.score}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PredictiveMaintenancePage;
